import AutocompleteStyled from "@/components/AutocompleteStyled";
import { ContentContainer } from "@/components/ContentContainer";
import { LocationCard } from "@/components/network";
import NetworkLocationFormAgencies from "@/components/network/form/NetworkLocationFormAgencies";
import NetworkLocationFormBands from "@/components/network/form/NetworkLocationFormBands";
import NetworkLocationFormBase from "@/components/network/form/NetworkLocationFormBase";
import NetworkLocationFormClubs from "@/components/network/form/NetworkLocationFormClubs";
import NetworkLocationFormContact from "@/components/network/form/NetworkLocationFormContact";
import NetworkLocationFormFestivals from "@/components/network/form/NetworkLocationFormFestivals";
import NetworkLocationFormHochschulen from "@/components/network/form/NetworkLocationFormHochschulen";
import NetworkLocationFormLabels from "@/components/network/form/NetworkLocationFormLabels";
import NetworkLocationFormLegal from "@/components/network/form/NetworkLocationFormLegal";
import NetworkLocationFormMusikschulen from "@/components/network/form/NetworkLocationFormMusikschulen";
import NetworkLocationFormMusikverlage from "@/components/network/form/NetworkLocationFormMusikverlage";
import NetworkLocationFormPlattenvertriebe from "@/components/network/form/NetworkLocationFormPlattenvertriebe";
import NetworkLocationFormProberaumzentren from "@/components/network/form/NetworkLocationFormProberaumzentren";
import NetworkLocationFormSocialMedia from "@/components/network/form/NetworkLocationFormSocialMedia";
import NetworkLocationFormStudios from "@/components/network/form/NetworkLocationFormStudios";
import AppContext from "@/context/AppContext";
import {
  createLocation,
  createPopboardBand,
  useLocationCategories,
} from "@/lib/api";
import theme from "@/styles/theme";
import { Button, Skeleton } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import Router from "next/router";
import { useContext, useEffect, useMemo, useState } from "react";

export default function Create() {
  const { setSnackbar } = useContext(AppContext);
  const { categories, isLoading } = useLocationCategories();
  const [formValues, setFormValues] = useState({ source: "user" });

  const augmentedCategories = useMemo(() => {
    if (!categories) {
      return;
    }

    const result = [
      ...categories,
      {
        id: "bands",
        name: "Musiker*innen",
      },
    ];

    return result;
  }, [categories]);

  useEffect(() => {
    let newAddress = `${formValues.street ?? ""}\{pb}${
      formValues.zipcode
        ? `${formValues.zipcode.zipcode} ${formValues.zipcode.label}`
        : ""
    }`;
    newAddress = newAddress.replaceAll(
      "{pb}",
      newAddress.trim().length > 4 ? "\r\n" : "",
    );

    setFormValues({
      ...formValues,
      ["address"]: newAddress,
    });
  }, [formValues?.street, formValues?.zipcode]);

  const handleInputChanged = (event, value) => {
    const parsedValue =
      event.target.type === "number"
        ? Number(value ?? event.target.value)
        : (value ?? event.target.value);

    setFormValues({ ...formValues, [event.target.name]: parsedValue });
  };

  const handleCategorySelected = (event, value) => {
    setFormValues({ ...formValues, ["category"]: value });
  };

  const CATEGORY_FRAGMENT = {
    bands: <NetworkLocationFormBands onChange={handleInputChanged} />,
    1: <NetworkLocationFormClubs onChange={handleInputChanged} />,
    5: <NetworkLocationFormProberaumzentren onChange={handleInputChanged} />,
    6: <NetworkLocationFormLabels onChange={handleInputChanged} />,
    7: <NetworkLocationFormStudios onChange={handleInputChanged} />,
    9: <NetworkLocationFormMusikverlage onChange={handleInputChanged} />,
    10: <NetworkLocationFormMusikschulen onChange={handleInputChanged} />,
    12: <NetworkLocationFormFestivals onChange={handleInputChanged} />,
    14: <NetworkLocationFormHochschulen onChange={handleInputChanged} />,
    16: <NetworkLocationFormPlattenvertriebe onChange={handleInputChanged} />,
    18: <NetworkLocationFormAgencies onChange={handleInputChanged} />,
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    let dataToSave = formValues;

    dataToSave.zipcode = formValues.zipcode.id;
    dataToSave.category = formValues.category.id;
    dataToSave.location_subcategory = dataToSave.location_subcategory?.id;

    let genres = [];
    for (const g of formValues.genres ?? []) {
      genres = [...genres, g.id];
    }
    dataToSave.genres = genres;

    let degress = [];
    for (const d of formValues.degrees ?? []) {
      degress = [...degress, d.id];
    }
    dataToSave.university_degrees = degress;

    let studiengangs = [];
    for (const s of formValues.studiengangs ?? []) {
      studiengangs = [...studiengangs, s.id];
    }
    dataToSave.studiengangs = studiengangs;

    for (const k in dataToSave) {
      if (dataToSave[k] === "true") {
        dataToSave[k] = true;
      } else if (dataToSave[k] === "false") {
        dataToSave[k] = false;
      }
    }

    dataToSave["creator_consent_datashare"] = new Date();

    console.debug("Data to save", dataToSave);

    if (dataToSave.category === "bands") {
      dataToSave["source"] = "form";
      await createPopboardBand(dataToSave);
    } else {
      await createLocation(dataToSave);
    }

    Router.push("/netzwerk/locations/success");

    setFormValues({ source: "user" });
  };

  if (isLoading) {
    return <Skeleton variant="rectangular" height={101} />;
  }

  return (
    <>
      <ContentContainer headline="Neuen Eintrag vorschlagen">
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" textAlign="center">
              Fehlt was auf unserer Map?
            </Typography>
            <Typography variant="body1" textAlign="center" sx={{ mt: 4 }}>
              Pop ist wie ein Rummelplatz und ist immer in Bewegung. Logisch,
              dass eine Map da nie komplett sein kann und wir darauf angewiesen
              sind, euren Input zu erhalten. Bitte tragt alles so detailliert
              wie möglich ein, damit wir alle gemeinsam eine Map haben, die so
              aktuell wie nur möglich ist, um uns weiterzuhelfen.
            </Typography>
          </Grid>
        </Grid>
        <Grid container spacing={3} sx={style}>
          <Grid item xs={12} md={8}>
            <form onSubmit={handleSubmit}>
              <Typography variant="h4" component="p" color="common.black">
                Kategorie
              </Typography>
              <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
                Wähle hier die Kategorie für deine Location aus.
              </Typography>
              <AutocompleteStyled
                required
                label="Kategorie"
                options={augmentedCategories}
                placeholder="Bitte wählen"
                onChange={handleCategorySelected}
                getOptionLabel={(option) => `${option.name}`}
              />

              {formValues.category?.id &&
                CATEGORY_FRAGMENT[formValues.category.id]}

              <NetworkLocationFormBase
                formValues={formValues}
                onChange={handleInputChanged}
              />

              <NetworkLocationFormSocialMedia
                formValues={formValues}
                onChange={handleInputChanged}
              />

              <NetworkLocationFormContact onChange={handleInputChanged} />

              <NetworkLocationFormLegal onChange={handleInputChanged} />

              <Button
                type="submit"
                variant="contained"
                color="secondary"
                fullWidth
                sx={{ mt: 3 }}
              >
                Jetzt Eintrag vorschlagen
              </Button>
            </form>
          </Grid>
          {formValues?.category?.id !== "bands" && (
            <Grid item xs={12} sm={8} md={4}>
              <Box className="preview-box">
                <Typography variant="h4" component="p" color="common.black">
                  Vorschau
                </Typography>
                <Typography variant="body1" className="preview-text">
                  So wird dein Eintrag aussehen
                </Typography>
                <LocationCard location={formValues} page="Create" />
              </Box>
            </Grid>
          )}
        </Grid>
      </ContentContainer>
    </>
  );
}

const style = {
  "& .preview-box": {
    [theme.breakpoints.up("md")]: {
      top: "7rem",
      position: "sticky",
    },
    [theme.breakpoints.up("lg")]: {
      top: "10rem",
    },
  },
  "& .preview-text": {
    color: grey[600],
    textTransform: "uppercase",
    mt: 2,
    mb: 0.5,
    fontSize: "0.875rem",
    [theme.breakpoints.up("md")]: {
      mt: 5.8,
    },
  },
};
